'use client';
import { useState } from 'react';
import Slider, { Settings } from 'react-slick';
import { InView } from 'react-intersection-observer';
import Image from 'next/image';
import { usePathname } from 'next/navigation';

import { cn } from '@/lib/utils';
import getMediaFilterUrl from '@/utils/get-media-filter';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';

import {
  ExperienceMedia,
  ExperienceTranslation,
  VillageExperience,
} from '@/services/types';
import useObserverContext from '@/hooks/useObserverContext';

import './styles.css';
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';
import { sicilyMap } from '@/utils/maps';

type Props = {
  lang: string;
  experiences: VillageExperience[];
};

type CarouselItemProps = {
  translation: ExperienceTranslation;
  image: ExperienceMedia;
  lang: string;
  macroCategoryTranslation: {
    id: number;
    title: string;
    description: string;
    locale: string;
    slug: string;
    category: number;
  };
};

export default function VillageExperiencesSectionCarousel({
  lang,
  experiences,
}: Props) {
  const [sliderRef, setSliderRef] = useState<Slider | null>(null);
  const [slickIndex, setSlickIndex] = useState<number>(0);
  const { handleCurrentSection } = useObserverContext();

  const formatedExperiences = new Array<any>();

  experiences.flatMap(({ experience }) => {
    const { experienceTranslation = [], macroCategory } = experience;

    const translation =
      experienceTranslation.find(
        (translation) => translation.locale === lang,
      ) || experienceTranslation[0];

    const macroCategoryTranslation =
      macroCategory.translation.find(
        (translation) => translation.locale === lang,
      ) || macroCategory.translation[0];

    const coverImage =
      experience.media.filter((media) => media.type === 'cover') || [];

    if (coverImage[0])
      formatedExperiences.push({
        translation,
        image: coverImage[0],
        macroCategoryTranslation,
      });
  });

  const settings: Settings = {
    dots: false,
    infinite: false,
    speed: 1500,
    slidesToShow: 4,
    slidesToScroll: 1,
    beforeChange: (prev: number, next: number) => {
      setSlickIndex(next);
    },
    responsive: [
      {
        breakpoint: 1200,
        settings: {
          slidesToShow: 2,
          speed: 1000,
          infinite: formatedExperiences.length > 2,
          beforeChange: (prev: number, next: number) => {
            setSlickIndex(next);
          },
        },
      },
      {
        breakpoint: 767,
        settings: {
          slidesToShow: 1,
          speed: 1500,
          infinite: formatedExperiences.length > 1,
          beforeChange: (prev: number, next: number) => {
            setSlickIndex(next);
          },
        },
      },
    ],
  };

  const itemsLastIndex = Math.floor(formatedExperiences.length / 4);

  function handleInViewOnChange(inView: boolean) {
    if (inView) {
      handleCurrentSection('experiences');
    } else {
      handleCurrentSection('');
    }
  }

  return (
    <InView
      as="div"
      onChange={(inView) => handleInViewOnChange(inView)}
      className="w-full relative">
      <button
        id="ve-carousel-previous-button"
        aria-label="Carousel previous button"
        className={cn(
          'rounded-full z-10 text-zinc-400 flex justify-center group items-center bg-white shadow-lg overflow-hidden absolute top-[40%] md:top-[35%] left-0  translate-y-[-50%] mt-[60px] w-11 h-[44px] desktop:w-[64px] desktop:h-[64px] desktop:left-[calc(0%-2.5rem)] transition-opacity duration-300 ease-in-out desktop:opacity-1 desktop:scale-100',
          {
            'desktop:opacity-0 desktop:scale-0': slickIndex === 0,
          },
        )}
        type="button"
        onClick={sliderRef?.slickPrev}>
        <FiChevronLeft className="w-6 h-6 desktop:w-8 desktop:h-8 stroke-slate-600 opacity-30 group-hover:opacity-20 transition" />
      </button>

      <button
        id="ve-carousel-next-button"
        aria-label="Carousel next button"
        className={cn(
          'rounded-full z-10 text-zinc-400 flex justify-center group items-center bg-white shadow-lg overflow-hidden absolute top-[40%] md:top-[35%] right-0 translate-y-[-50%] mt-[60px] w-11 h-[44px] desktop:w-[64px] desktop:h-[64px] desktop:right-[-2.5rem] desktop:opacity-1 desktop:scale-100',
          {
            'desktop:opacity-0 desktop:scale-0': slickIndex === itemsLastIndex,
          },
        )}
        type="button"
        onClick={sliderRef?.slickNext}>
        <FiChevronRight className="w-6 h-6 desktop:w-8 desktop:h-8 stroke-slate-600 opacity-30 group-hover:opacity-20 transition" />
      </button>

      <Slider
        ref={setSliderRef}
        className={cn('slick-container mt-[60px] mx-[-0.9375rem] md:mx-0')}
        {...settings}>
        {formatedExperiences.map((experience, key) => (
          <CarouselItem
            key={key}
            lang={lang}
            translation={experience.translation}
            image={experience.image}
            macroCategoryTranslation={experience.macroCategoryTranslation}
          />
        ))}
      </Slider>
    </InView>
  );
}

function CarouselItem({
  translation = {} as ExperienceTranslation,
  image,
  lang,
  macroCategoryTranslation,
}: CarouselItemProps) {
  const pathname = usePathname();

  const imgCover =
    getMediaFilterUrl({
      mediaFilters: image.mediaFilters,
      filterName: 'vertical_slider_2x_webp',
    }) ??
    getMediaFilterUrl({
      mediaFilters: image.mediaFilters,
      filterName: '1920w',
    }) ??
    '';

  const experienceMap: { [key: string]: string } = {
    it: 'esperienze',
    fr: 'experiences',
    de: 'erfahrungen',
  };

  function getUrl() {
    let langString = lang === 'en' ? '' : '/' + lang;
    let sicilyTranslation = 'sicily';
    let experiencesTranslation = 'experiences';
    const macroCategoryCodeSlug = macroCategoryTranslation.slug;
    const categorySlug = translation.slug;

    if (lang !== 'en') {
      sicilyTranslation = sicilyMap[lang];
      experiencesTranslation = experienceMap[lang];
    }

    return `${langString}/${sicilyTranslation}/${experiencesTranslation}/${macroCategoryCodeSlug}/${categorySlug}`;
  }

  return (
    <a tabIndex={-1} className={cn('c-card', '')} href={getUrl()}>
      <div className="c-card_bg">
        <Image
          src={imgCover}
          alt={translation.title}
          fill
          sizes="(min-width: 1280px) 80vw, (min-width: 767px) 90vw, (min-width: 480px) 100vw, 100vw"
          style={{
            objectFit: 'cover',
          }}
        />
      </div>
      <div className="c-card_content">
        <h3 className="mb-2 mt-0 relative font-semibold text-xl text-center">
          {translation.title}
        </h3>
        <div
          className="card-subtitle overflow-hidden mt-[20px] h-[96px] relative text-base text-center [&>p]:mb-8"
          dangerouslySetInnerHTML={{ __html: translation.description }}
        />
      </div>
    </a>
  );
}
